import { useState, useEffect } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const Field = ({ label, value, onChange, rows, testId, max }) => (
  <div>
    <Label className="text-xs">{label}{max && <span className="text-slate-400 font-normal"> · {(value || "").length}/{max}</span>}</Label>
    {rows ? <Textarea rows={rows} value={value} maxLength={max} onChange={e => onChange(e.target.value)} data-testid={testId} />
      : <Input value={value} maxLength={max} onChange={e => onChange(e.target.value)} data-testid={testId} />}
  </div>
);

const ListEditor = ({ title, items, setItems, a, b, blank, testId }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between"><p className="text-sm font-bold text-slate-900">{title}</p>
      <button onClick={() => setItems([...items, blank])} className="text-xs text-blue-600 font-medium flex items-center gap-0.5" data-testid={`${testId}-add`}><Plus className="w-3.5 h-3.5" />Add</button></div>
    {items.map((it, i) => (
      <div key={i} className="border border-slate-200 rounded-lg p-2.5 space-y-1.5 relative">
        <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-slate-300 hover:text-red-600" data-testid={`${testId}-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
        <Input value={it[a[0]]} onChange={e => setItems(items.map((x, j) => j === i ? { ...x, [a[0]]: e.target.value } : x))} placeholder={a[1]} className="h-8 text-sm font-semibold pr-8" data-testid={`${testId}-${a[0]}-${i}`} />
        <Textarea rows={3} value={it[b[0]]} onChange={e => setItems(items.map((x, j) => j === i ? { ...x, [b[0]]: e.target.value } : x))} placeholder={b[1]} className="text-sm" data-testid={`${testId}-${b[0]}-${i}`} />
      </div>
    ))}
  </div>
);

export const LandingPageEditor = ({ pageId, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/api/webadvisor/landing-page/${pageId}`).then(({ data: p }) => setF({
      title: p.title || "", meta_title: p.meta_title || "", meta_description: p.meta_description || "",
      h1: p.content?.h1 || "", intro: p.content?.intro || "", cta: p.content?.cta || "", sections: p.content?.sections || [], faqs: p.content?.faqs || [],
    })).catch(() => { toast.error("Couldn't load the page"); onClose(); });
  }, [pageId, onClose]);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/api/webadvisor/landing-page/${pageId}/content`, f); toast.success("Page saved"); onSaved(); onClose(); }
    catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lp-editor">
        <DialogHeader><DialogTitle>Edit landing page</DialogTitle></DialogHeader>
        {!f ? <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto my-8" /> : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Page title" value={f.title} onChange={set("title")} testId="lp-edit-title" max={150} />
              <Field label="Headline (H1)" value={f.h1} onChange={set("h1")} testId="lp-edit-h1" max={150} />
              <Field label="SEO title" value={f.meta_title} onChange={set("meta_title")} testId="lp-edit-meta-title" max={70} />
              <Field label="Call-to-action button" value={f.cta} onChange={set("cta")} testId="lp-edit-cta" max={80} />
            </div>
            <Field label="SEO description" value={f.meta_description} onChange={set("meta_description")} rows={2} testId="lp-edit-meta-desc" max={170} />
            <Field label="Intro" value={f.intro} onChange={set("intro")} rows={3} testId="lp-edit-intro" max={800} />
            <ListEditor title="Sections" items={f.sections} setItems={set("sections")} a={["heading", "Section heading"]} b={["body", "Section text"]} blank={{ heading: "", body: "" }} testId="lp-edit-section" />
            <ListEditor title="FAQs" items={f.faqs} setItems={set("faqs")} a={["q", "Question"]} b={["a", "Answer"]} blank={{ q: "", a: "" }} testId="lp-edit-faq" />
            <button onClick={save} disabled={busy || !f.title.trim()} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="lp-edit-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save page
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
